import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeftRight, CalendarClock, Check, Clock, HandHeart, Music, Plus, ScanLine, Shirt, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, errMsg, fmtDate, fileUrl } from "@/lib/api";
import { PageTitle, useApi, Skeleton, Stagger, Item, badge, inputCls, btnPrimary } from "@/components/portal/ui";
import { QrScanner } from "@/components/portal/QrScanner";
import { CheckInFlipCard } from "@/components/portal/CheckInFlipCard";
import MinistryLeaderboard from "@/pages/portal/Leaderboard";
import MyAwards from "@/pages/portal/Piagam";

function DressCodeModal({ m, onClose }) {
  const sections = m.dress_sections?.length ? m.dress_sections : [{ label: "Umum", dress_code: m.dress_code, dress_images: m.dress_images }];
  return createPortal(
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="dresscode-modal" onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-5 flex items-start justify-between">
          <div><p className="text-[11px] font-black uppercase tracking-widest text-amber-300">Dress Code</p><p className="font-extrabold mt-0.5">{m.ministry_name}</p></div>
          <button onClick={onClose} data-testid="dresscode-close" className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          {sections.every((s) => !s.dress_code && !(s.dress_images || []).length) && (
            <p className="text-sm text-slate-400">Admin belum mengatur dress code untuk pelayanan ini.</p>
          )}
          {sections.map((sec, idx) => (sec.dress_code || (sec.dress_images || []).length > 0) && (
            <div key={idx} data-testid={`dresscode-section-${idx}`}>
              {sections.length > 1 && <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-1.5">{sec.label}</p>}
              {sec.dress_code && <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{sec.dress_code}</p>}
              {(sec.dress_images || []).length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {sec.dress_images.map((img, i) => <img key={i} src={fileUrl(img)} alt="referensi busana" className="w-full rounded-xl border border-slate-100 object-cover" data-testid={`dresscode-image-${idx}-${i}`} />)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>, document.body);
}

function TeamModal({ s, onClose }) {
  const { data } = useApi(`/portal/ministry/schedules/${s.id}/team`);
  return createPortal(
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="team-modal" onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-5 flex items-start justify-between">
          <div><p className="text-[11px] font-black uppercase tracking-widest text-amber-300">Tim Pelayanan</p><p className="font-extrabold mt-0.5">{s.service} · {fmtDate(s.date, { day: "numeric", month: "short" })}</p></div>
          <button onClick={onClose} data-testid="team-modal-close" className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {!data ? <Skeleton className="h-24" /> : data.team.length === 0 ? (
            <p className="text-sm text-slate-400">Belum ada rekan lain yang terjadwal.</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {data.team.map((t, i) => (
                <div key={i} className="py-2.5 flex items-center justify-between" data-testid={`team-member-${i}`}>
                  <span className="font-bold text-slate-900 text-sm">{t.name}</span>
                  <span className="text-xs text-slate-500 font-semibold">{t.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>, document.body);
}

const STATUS = { assigned: ["warn", "Perlu konfirmasi"], confirmed: ["success", "Siap melayani"], declined: ["danger", "Berhalangan"] };

function ScheduleCard({ s, colleagues, onAction, reload }) {
  const [swap, setSwap] = useState(false);
  const [to, setTo] = useState("");
  const [scanning, setScanning] = useState(false);
  const [flip, setFlip] = useState(null);
  const [team, setTeam] = useState(false);
  const [tone, label] = STATUS[s.status] || ["info", s.status];
  const peers = colleagues.filter((c) => c.ministry_id === s.ministry_id);

  const onScanAttendance = async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    setScanning(false);
    setFlip({ stage: "checking" });
    try {
      await api.post(`/portal/ministry/schedules/${s.id}/scan-attendance`, { code });
      setFlip({ stage: "done", icon: HandHeart, title: "Selamat Melayani!", sub: `${s.service} · ${s.role}`, tone: "amber" });
      reload();
    } catch (e) {
      setFlip(null);
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid={`ministry-schedule-${s.id}`}>
      <div className="flex items-center justify-between"><span className={`text-[11px] font-bold rounded-full px-3 py-1 ${badge[tone]}`}>{label}</span><span className="text-xs text-slate-500">{s.role}</span></div>
      <p className="mt-3 font-extrabold text-slate-900">{s.service}</p>
      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Clock className="w-3.5 h-3.5" />{fmtDate(s.date, { weekday: "long", day: "numeric", month: "short" })} · Call time {s.call_time}</p>
      {s.swapped_from && <p className="text-[11px] text-indigo-600 font-semibold mt-1">Hasil tukar jadwal</p>}
      <div className="mt-4 flex gap-2">
        {!s.attended && s.status !== "confirmed" && <button onClick={() => onAction("confirm", s, { status: "confirmed" })} data-testid={`ministry-confirm-${s.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-500 text-white rounded-xl py-2.5 text-sm font-bold active:scale-95 transition"><Check className="w-4 h-4" />Hadir</button>}
        {!s.attended && s.status !== "declined" && <button onClick={() => onAction("confirm", s, { status: "declined" })} data-testid={`ministry-decline-${s.id}`} className="inline-flex items-center justify-center bg-red-50 text-red-600 rounded-xl px-3 active:scale-95 transition"><X className="w-4 h-4" /></button>}
        {!s.attended && <button onClick={() => setSwap(!swap)} data-testid={`ministry-swap-toggle-${s.id}`} className="inline-flex items-center justify-center gap-1.5 border border-slate-200 rounded-xl px-3 text-sm font-bold text-slate-700 active:scale-95 transition"><ArrowLeftRight className="w-4 h-4" />Tukar</button>}
        {s.attended && (
          <span className="flex-1 inline-flex items-center justify-center gap-1.5 bg-amber-50 text-amber-700 rounded-xl py-2.5 text-sm font-bold" data-testid={`ministry-attended-${s.id}`}>
            <HandHeart className="w-4 h-4" />Kehadiran Tercatat
          </span>
        )}
      </div>
      <button onClick={() => setTeam(true)} data-testid={`ministry-team-${s.id}`} className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600"><Users className="w-3.5 h-3.5" />Tim Pelayanan</button>
      {swap && (
        <div className="mt-3 flex gap-2">
          <select className={`${inputCls} !py-2.5 text-sm`} value={to} onChange={(e) => setTo(e.target.value)} data-testid={`ministry-swap-select-${s.id}`}>
            <option value="">Pilih rekan pelayanan…</option>{peers.map((c) => <option key={c.user_id} value={c.user_id}>{c.name} · {c.role}</option>)}
          </select>
          <button disabled={!to} onClick={() => onAction("swap", s, { to_user_id: to }).then(() => setSwap(false))} data-testid={`ministry-swap-send-${s.id}`} className="shrink-0 bg-blue-600 text-white rounded-xl px-4 text-sm font-bold disabled:opacity-50">Kirim</button>
        </div>
      )}
      {s.status === "confirmed" && !s.attended && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          {!scanning ? (
            <button onClick={() => setScanning(true)} data-testid={`ministry-scan-attendance-${s.id}`} className="w-full inline-flex items-center justify-center gap-1.5 bg-indigo-600 text-white rounded-xl py-2.5 text-sm font-bold active:scale-95 transition"><ScanLine className="w-4 h-4" />Scan Barcode Kehadiran</button>
          ) : (
            <div className="space-y-2">
              <QrScanner onScan={onScanAttendance} testId={`ministry-attendance-scanner-${s.id}`} transform={(t) => t.replace(/^GIM-MIN:/, "")} />
              <button type="button" onClick={() => setScanning(false)} data-testid={`ministry-scan-cancel-${s.id}`} className="w-full text-xs font-bold text-slate-500 py-1">Batal</button>
            </div>
          )}
        </div>
      )}
      <CheckInFlipCard flip={flip} onFinished={() => setFlip(null)} />
      {team && <TeamModal s={s} onClose={() => setTeam(false)} />}
    </div>
  );
}

function SongBankModal({ m, onClose }) {
  const { data, reload } = useApi(`/portal/ministry/${m.ministry_id}/songs`);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: r } = await api.post(`/portal/ministry/${m.ministry_id}/songs`, { title, link });
      toast.success(r.overwritten ? "Link lagu diperbarui" : "Lagu ditambahkan");
      setTitle(""); setLink(""); reload();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/portal/ministry/songs/${id}`); reload(); } catch (e2) { toast.error(errMsg(e2)); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="songbank-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-5 flex items-start justify-between">
          <div><p className="text-[11px] font-black uppercase tracking-widest text-amber-300">Song Bank</p><p className="font-extrabold mt-0.5">{m.ministry_name}</p></div>
          <button onClick={onClose} data-testid="songbank-close" className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {!data ? <Skeleton className="h-24" /> : (
            <>
              {data.can_manage && (
                <form onSubmit={add} className="flex flex-col gap-2 mb-4" data-testid="songbank-add-form">
                  <input required className={inputCls} placeholder="Judul lagu" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="songbank-title-input" />
                  <input required className={inputCls} placeholder="Link referensi (YouTube/Spotify)" value={link} onChange={(e) => setLink(e.target.value)} data-testid="songbank-link-input" />
                  <button disabled={saving} className={`${btnPrimary} !py-2.5 text-sm`} data-testid="songbank-add-button"><Plus className="w-4 h-4" />Tambah Lagu</button>
                </form>
              )}
              <div className="divide-y divide-slate-50">
                {data.songs.map((s) => (
                  <div key={s.id} className="py-2.5 flex items-center justify-between gap-2" data-testid={`songbank-song-${s.id}`}>
                    <a href={s.link} target="_blank" rel="noreferrer" className="min-w-0 font-bold text-sm text-slate-900 hover:text-indigo-600 truncate">{s.title}</a>
                    {data.can_manage && <button onClick={() => remove(s.id)} data-testid={`songbank-delete-${s.id}`} className="shrink-0 text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>}
                  </div>
                ))}
                {data.songs.length === 0 && <p className="py-5 text-sm text-slate-400">Belum ada lagu di song bank ini.</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>, document.body);
}

const MEETING_EMPTY = { title: "", date: "", time: "19:00", location: "", note: "" };

function MeetingScanner() {
  const [value, setValue] = useState("");
  const [camera, setCamera] = useState(false);
  const submit = async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    try {
      const { data } = await api.post("/portal/internal-meetings/scan", { code });
      toast.success(`Hadir dicatat · ${data.name}`);
      setValue("");
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <div className="pb-4 mb-1 border-b border-slate-100 space-y-2" data-testid="meetings-scanner">
      <form onSubmit={(e) => { e.preventDefault(); submit(value); }} className="flex gap-2">
        <input className={inputCls} value={value} onChange={(e) => setValue(e.target.value)} placeholder="Kode tiket (IM-…) peserta" data-testid="meetings-scan-input" />
        <button className={`${btnPrimary} !py-2.5`} data-testid="meetings-scan-submit"><ScanLine className="w-4 h-4" /></button>
      </form>
      <button type="button" onClick={() => setCamera(!camera)} data-testid="meetings-camera-toggle" className="w-full text-xs font-bold text-indigo-600">{camera ? "Matikan Kamera" : "Scan QR dengan Kamera"}</button>
      {camera && <QrScanner onScan={submit} testId="meetings-qr-reader" />}
    </div>
  );
}

function MeetingsModal({ m, onClose }) {
  const canManage = m.can_manage_songs;
  const { data: mine, reload: reloadMine } = useApi("/portal/internal-meetings");
  const [form, setForm] = useState(MEETING_EMPTY);
  const [saving, setSaving] = useState(false);
  const myList = (mine?.meetings || []).filter((x) => x.scope === "ministry" && x.ministry_id === m.ministry_id);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/portal/internal-meetings/ministry/${m.ministry_id}/manage`, form);
      toast.success("Pertemuan dibuat & anggota divisi dinotifikasi");
      setForm(MEETING_EMPTY);
      reloadMine();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/portal/internal-meetings/ministry/${m.ministry_id}/manage/${id}`); reloadMine(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const register = async (id) => {
    try { await api.post(`/portal/internal-meetings/${id}/register`); toast.success("Terdaftar! Tiket QR-mu sudah siap"); reloadMine(); } catch (e2) { toast.error(errMsg(e2)); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="meetings-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-5 flex items-start justify-between">
          <div><p className="text-[11px] font-black uppercase tracking-widest text-amber-300">Pertemuan Internal</p><p className="font-extrabold mt-0.5">{m.ministry_name}</p></div>
          <button onClick={onClose} data-testid="meetings-close" className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {canManage && <MeetingScanner />}
          {canManage && (
            <form onSubmit={create} className="flex flex-col gap-2 pb-4 border-b border-slate-100" data-testid="meetings-create-form">
              <input required className={inputCls} placeholder="Judul pertemuan" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meetings-title-input" />
              <input required type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="meetings-date-input" />
              <input type="time" className={inputCls} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="meetings-time-input" />
              <input className={inputCls} placeholder="Lokasi" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="meetings-location-input" />
              <button disabled={saving} className={`${btnPrimary} !py-2.5 text-sm`} data-testid="meetings-create-button"><Plus className="w-4 h-4" />Buat Pertemuan</button>
            </form>
          )}
          {!mine ? <Skeleton className="h-24" /> : myList.length === 0 ? (
            <p className="text-sm text-slate-400">Belum ada pertemuan internal dijadwalkan untuk divisi ini.</p>
          ) : (
            <div className="space-y-3">
              {myList.map((item) => (
                <div key={item.id} className="rounded-2xl border border-slate-100 p-4" data-testid={`meeting-item-${item.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 text-sm truncate">{item.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{fmtDate(item.date, { weekday: "long", day: "numeric", month: "long" })} · {item.time} WITA{item.location ? ` · ${item.location}` : ""}</p>
                    </div>
                    {canManage && <button onClick={() => remove(item.id)} data-testid={`meeting-delete-${item.id}`} className="shrink-0 text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  {item.ticket_code ? (
                    <div className="mt-3 rounded-2xl bg-slate-900 text-white p-3 flex items-center gap-3">
                      <span className="bg-white p-1.5 rounded-lg shrink-0"><QRCodeSVG value={`GIM-IM:${item.ticket_code}`} size={60} /></span>
                      <div className="min-w-0"><p className="text-[10px] font-black uppercase tracking-widest text-amber-300">Tiket Saya</p>{item.scanned_at && <p className="mt-0.5 text-[10px] font-bold text-emerald-300">Kehadiran tercatat</p>}</div>
                    </div>
                  ) : (
                    <button onClick={() => register(item.id)} data-testid={`meeting-register-${item.id}`} className="mt-3 w-full text-xs font-bold text-indigo-600 bg-indigo-50 rounded-xl py-2.5 hover:bg-indigo-100 active:scale-95 transition">Daftar (Saya Hadir)</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>, document.body);
}

export default function MyMinistry() {
  const { data, reload } = useApi("/portal/ministry");
  const { data: cat } = useApi("/portal/ministry/categories");
  const { data: usher } = useApi("/portal/usher/assignment");
  const [dress, setDress] = useState(null);
  const [songs, setSongs] = useState(null);
  const [meetings, setMeetings] = useState(null);

  const onAction = async (kind, s, body) => {
    try {
      await api.post(`/portal/ministry/schedules/${s.id}/${kind}`, body);
      toast.success(kind === "swap" ? "Permintaan tukar terkirim" : "Konfirmasi tersimpan");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const respond = async (sw, accept) => {
    try {
      await api.post(`/portal/ministry/swaps/${sw.id}/respond`, { accept });
      toast.success(accept ? "Jadwal otomatis dipindahkan ke Anda" : "Permintaan ditolak");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="portal-ministry">
      <PageTitle eyebrow="Serving" title="My Ministry" />
      <div className="px-5 mt-2">
        <Link to="/portal/serve-up" data-testid="ministry-serve-up-link" className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-2xl py-3 text-sm font-bold active:scale-95 transition"><HandHeart className="w-4 h-4 text-amber-300" />Serve-Up · Daftar Pelayanan</Link>
      </div>
      {usher?.is_usher ? (
        <div className="px-5 mt-3">
          <Link to="/portal/usher-scan" data-testid="ministry-usher-link" className="block rounded-3xl bg-blue-600 text-white p-5 active:scale-[0.99] transition">
            <ScanLine className="w-6 h-6 text-blue-100" />
            <p className="mt-3 font-extrabold">Scanner Usher</p>
            <p className="text-xs text-white/70 mt-0.5">Anda bertugas{usher.role ? ` sebagai ${usher.role}` : ""}. Pindai QR jemaat & tiket event di sini.</p>
          </Link>
        </div>
      ) : usher?.reason ? (
        <div className="px-5 mt-3">
          <div className="rounded-3xl bg-white border border-slate-200 p-5" data-testid="ministry-usher-locked">
            <ScanLine className="w-6 h-6 text-slate-300" />
            <p className="mt-3 font-extrabold text-slate-900">Scanner Usher Terkunci</p>
            <p className="text-xs text-slate-500 mt-0.5">{usher.reason}</p>
          </div>
        </div>
      ) : null}
      {cat?.applications?.filter((a) => a.status === "pending").map((a) => (
        <div key={a.id} className="px-5 mt-3" data-testid={`ministry-pending-${a.id}`}>
          <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800 font-semibold">Menunggu persetujuan: {a.ministry_name}{a.role ? ` · ${a.role}` : ""}</div>
        </div>
      ))}
      <div className="px-5 mt-4">
        {!data ? <Skeleton className="h-64" /> : (
          <Stagger className="space-y-4">
            {data.ministries.length === 0 && <Item className="bg-white rounded-3xl p-6 border border-slate-100 text-sm text-slate-500" data-testid="ministry-empty">Anda belum terdaftar di divisi pelayanan. Daftar lewat menu <b>Serve-Up</b> di atas.</Item>}
            {data.ministries.length > 0 && (
              <Item className="space-y-3">
                {data.ministries.map((m) => (
                  <div key={m.id} className="rounded-3xl p-5 bg-slate-900 text-white aspect-video flex flex-col overflow-hidden" data-testid={`ministry-${m.ministry_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-blue-300"><Users className="w-3.5 h-3.5" />Divisi Pelayanan</span>
                        <p className="mt-1 font-black text-lg leading-tight truncate">{m.ministry_name}</p>
                        <p className="text-xs text-white/60">{m.role}</p>
                      </div>
                      {m.scheduled ? (
                        <div className="shrink-0 flex flex-col items-end gap-1" data-testid={`ministry-scheduled-${m.ministry_id}`}>
                          {m.schedules.slice(0, 2).map((sc) => (
                            <span key={sc.id} className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-300 bg-emerald-500/15 rounded-full px-2 py-0.5 whitespace-nowrap"><CalendarClock className="w-3 h-3" />{fmtDate(sc.date, { day: "numeric", month: "short" })} · {sc.service}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="shrink-0 text-[11px] text-white/40" data-testid={`ministry-unscheduled-${m.ministry_id}`}>Belum ada jadwal</span>
                      )}
                    </div>
                    <div className="mt-auto pt-3 flex flex-wrap gap-1.5">
                      <button onClick={() => setDress(m)} data-testid={`ministry-dresscode-${m.ministry_id}`} className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 active:scale-95 transition"><Shirt className="w-3.5 h-3.5 text-amber-300" />Dress Code</button>
                      <button onClick={() => setSongs(m)} data-testid={`ministry-songbank-${m.ministry_id}`} className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 active:scale-95 transition"><Music className="w-3.5 h-3.5 text-amber-300" />Song Bank</button>
                      <button onClick={() => setMeetings(m)} data-testid={`ministry-meetings-${m.ministry_id}`} className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 active:scale-95 transition"><Users className="w-3.5 h-3.5 text-amber-300" />Pertemuan Internal</button>
                      <Link to={`/portal/ministry/jadwal?id=${m.ministry_id}&name=${encodeURIComponent(m.ministry_name)}`} data-testid={`ministry-calendar-${m.ministry_id}`} className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 active:scale-95 transition"><CalendarClock className="w-3.5 h-3.5 text-amber-300" />Jadwal Bulanan</Link>
                    </div>
                  </div>
                ))}
                <div className="rounded-3xl p-4 bg-blue-600 text-white flex items-center justify-between" data-testid="ministry-attendance"><span className="text-sm font-semibold">Kehadiran pelayanan</span><span className="text-2xl font-black">{data.attendance.served}/{data.attendance.total}</span></div>
              </Item>
            )}
            {data.swaps_in.map((sw) => (
              <Item key={sw.id} className="rounded-3xl p-5 bg-amber-100 text-amber-700" data-testid={`swap-in-${sw.id}`}>
                <p className="font-bold">{sw.from_name} minta tukar jadwal</p>
                <p className="text-sm mt-1">{sw.service} · {fmtDate(sw.date, { day: "numeric", month: "short" })} · {sw.role} · call {sw.call_time}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => respond(sw, true)} data-testid={`swap-accept-${sw.id}`} className="flex-1 bg-emerald-500 text-white rounded-xl py-2.5 text-sm font-bold">Terima</button>
                  <button onClick={() => respond(sw, false)} data-testid={`swap-reject-${sw.id}`} className="flex-1 bg-white text-slate-700 rounded-xl py-2.5 text-sm font-bold">Tolak</button>
                </div>
              </Item>
            ))}
            <Item><MyAwards /></Item>
            <Item><MinistryLeaderboard /></Item>
            {data.upcoming.length > 0 && <Item><p className="px-1 text-sm font-bold text-slate-900">Jadwal Pelayanan</p></Item>}            {data.upcoming.map((s) => <Item key={s.id}><ScheduleCard s={s} colleagues={data.colleagues} onAction={onAction} reload={reload} /></Item>)}
            {data.swaps_out.length > 0 && <Item><p className="px-1 text-sm font-bold text-slate-900">Permintaan Tukar Saya</p></Item>}
            {data.swaps_out.map((sw) => <Item key={sw.id} className="bg-white rounded-2xl p-4 border border-slate-100 text-sm flex justify-between"><span>{sw.service} → {sw.to_name}</span><span className="font-bold text-slate-500">{sw.status}</span></Item>)}
          </Stagger>
        )}
      </div>
      {dress && <DressCodeModal m={dress} onClose={() => setDress(null)} />}
      {songs && <SongBankModal m={songs} onClose={() => setSongs(null)} />}
      {meetings && <MeetingsModal m={meetings} onClose={() => setMeetings(null)} />}
    </div>
  );
}
